import { type ReactElement } from 'react';
import { Pencil, UserPlus } from 'lucide-react';
import type { Tenant } from '@kratispot/shared';
import { Button } from '@/components/ui/button';
import { useTranslation, type CopyKey } from '@/lib/i18n';
import { cn } from '@/lib/utils';

// Cover art per venue type; a kind nobody has styled yet still gets a card. §3
const COVERS: Readonly<Record<string, string>> = {
  beach_bar: 'bg-linear-to-b from-sky-400 via-sky-200 to-amber-100',
  beach: 'bg-linear-to-b from-sky-400 via-sky-200 to-amber-100',
  restaurant: 'bg-linear-to-br from-amber-400 to-amber-700',
  bar: 'bg-linear-to-br from-violet-500 to-violet-900',
  cafe: 'bg-linear-to-br from-orange-300 to-rose-500',
};

const DEFAULT_COVER = 'bg-linear-to-br from-cyan-600 to-cyan-900';

export function coverOf(venueType: string): string {
  return COVERS[venueType] ?? DEFAULT_COVER;
}

// Types are free text, so a chip shows the venue's own word for itself. §3
export function typeLabel(venueType: string): string {
  return venueType.replace(/[_-]+/g, ' ');
}

export function initialOf(name: string): string {
  return (name.trim().charAt(0) || '?').toUpperCase();
}

const OWNER_STATUS_KEY: Readonly<Record<string, CopyKey>> = {
  active: 'active',
  invited: 'invited',
  inactive: 'inactive',
};

export function VenueCard({
  tenant,
  onOpen,
  onEdit,
  onRegisterOwner,
}: {
  tenant: Tenant;
  onOpen: () => void;
  onEdit: () => void;
  onRegisterOwner: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const owners = tenant.owners;
  return (
    <li className="flex flex-col overflow-hidden rounded-2xl border bg-card shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      <div className={cn('relative h-26', coverOf(tenant.venueType))}>
        {tenant.logoUrl === null ? (
          <span className="absolute -bottom-3.5 left-3.5 grid size-11 place-items-center rounded-xl border-[3px] border-card bg-primary text-lg font-extrabold text-primary-foreground">
            {initialOf(tenant.name)}
          </span>
        ) : (
          <img
            src={tenant.logoUrl}
            alt=""
            className="absolute -bottom-3.5 left-3.5 size-11 rounded-xl border-[3px] border-card bg-card object-cover"
          />
        )}
        <span className="absolute top-2.5 right-2.5 rounded-full bg-background/90 px-2.5 py-1 text-[11px] font-extrabold text-emerald-800">
          {tenant.freeToday} {t('freeToday')}
        </span>
      </div>

      <div className="flex flex-1 flex-col gap-1.5 px-3.5 pt-6 pb-3.5">
        <span className="truncate text-sm font-extrabold">{tenant.name}</span>
        <span className="truncate text-xs text-muted-foreground">
          {typeLabel(tenant.venueType)}
          {tenant.location === null ? '' : ` · ${tenant.location}`} · {tenant.spotCount}{' '}
          {tenant.spotKind}
        </span>
        <span className="truncate text-xs text-muted-foreground">
          /{tenant.slug} ·{' '}
          {owners.length === 0
            ? t('ownerMissing')
            : owners
                .map(
                  (owner) =>
                    `${owner.name ?? owner.email} · ${t(OWNER_STATUS_KEY[owner.status] as CopyKey)}`,
                )
                .join(' · ')}
        </span>

        <div className="mt-auto flex flex-wrap gap-1.5 pt-2">
          <Button size="sm" className="flex-1 justify-center" onClick={onOpen}>
            {t('openVenue')}
          </Button>
          <Button size="sm" variant="outline" onClick={onEdit}>
            <Pencil className="size-4" aria-hidden />
            {t('edit')}
          </Button>
          <Button size="sm" variant="outline" onClick={onRegisterOwner}>
            <UserPlus className="size-4" aria-hidden />
            {t('manageOwners')}
          </Button>
        </div>
      </div>
    </li>
  );
}
